import type { MockDatabase } from "@/types/domain";
import type { MockRequest, MockResponse } from "@/mocks/router";
import { extras, recomputeGlobalStats, store } from "@/mocks/store";
import { err, genRippleId, matchPath, nowStamp, ok, paginate, pushAudit } from "./_utils";

export function handlePackages(req: MockRequest): MockResponse | null {
  const method = req.method.toUpperCase();
  const path = req.path.replace(/\/+$/, "") || "/";
  const { query, body } = req;

  if (method === "GET" && path === "/api/packages") {
    let items = [...store.PACKAGES];
    const gameId = query.get("gameId");
    if (gameId) items = items.filter((p) => p.game_id === gameId);
    return ok(paginate(items, query));
  }
  if (method === "POST" && path === "/api/packages") {
    const payload = body as Partial<MockDatabase["PACKAGES"][number]>;
    if (!payload.game_id) return err(422, "VALIDATION", "game_id 必填");
    const id = payload.id || genRippleId();
    if (store.PACKAGES.some((p) => p.id === id)) return err(409, "CONFLICT", "Ripple ID 冲突");
    const pkg = {
      id,
      game_id: payload.game_id,
      name: payload.name || "未命名包体",
      region: payload.region || "US",
      region_name: payload.region_name || payload.region || "US",
      platform: payload.platform || "iOS",
      bundle_id: payload.bundle_id || "",
      app_id: payload.app_id,
      store_url: payload.store_url || "",
      config_status: payload.config_status || "draft",
      today_share: payload.today_share || 0,
      today_install: payload.today_install || 0,
    };
    store.PACKAGES.push(pkg);
    const game = store.GAMES.find((g) => g.id === pkg.game_id);
    if (game) game.packages = store.PACKAGES.filter((p) => p.game_id === game.id).length;
    recomputeGlobalStats();
    pushAudit("创建包体", pkg.name, pkg.id);
    return ok({ data: pkg }, 201);
  }
  {
    const m = matchPath("/api/packages/:id/config-history", path);
    if (m && method === "GET") {
      const hist = extras.packageHistory[m.id] || [
        { id: "h1", at: nowStamp(), actor: "系统", action: "初始化配置", detail: "创建包体后默认 draft" },
      ];
      return ok({ data: hist, meta: { total: hist.length, page: 1, pageSize: 50 } });
    }
  }
  {
    const m = matchPath("/api/packages/:id", path);
    if (m && method === "GET") {
      const pkg = store.PACKAGES.find((p) => p.id === m.id);
      if (!pkg) return err(404, "NOT_FOUND", "包体不存在");
      return ok({ data: pkg });
    }
    if (m && method === "PATCH") {
      const idx = store.PACKAGES.findIndex((p) => p.id === m.id);
      if (idx < 0) return err(404, "NOT_FOUND", "包体不存在");
      store.PACKAGES[idx] = { ...store.PACKAGES[idx], ...(body as object) };
      extras.packageHistory[m.id] = extras.packageHistory[m.id] || [];
      extras.packageHistory[m.id].unshift({ id: `h_${Date.now()}`, at: nowStamp(), actor: store.ROLES[store.SESSION.currentRole]?.user, action: "更新包体", detail: JSON.stringify(body) });
      return ok({ data: store.PACKAGES[idx] });
    }
    if (m && method === "DELETE") {
      const idx = store.PACKAGES.findIndex((p) => p.id === m.id);
      if (idx < 0) return err(404, "NOT_FOUND", "包体不存在");
      const [removed] = store.PACKAGES.splice(idx, 1);
      const game = store.GAMES.find((g) => g.id === removed.game_id);
      if (game) game.packages = store.PACKAGES.filter((p) => p.game_id === game.id).length;
      recomputeGlobalStats();
      return ok({ data: removed });
    }
  }

  return null;
}
